import {useMutation} from "@tanstack/react-query";
import {login} from "api/auth.api.ts";
import {useAuth} from "context/AuthContext.jsx";
import {useToast} from "hooks/index.js";
import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import styled from "styled-components";
import {AppButton} from "ui/base/appButton/AppButton.jsx";
import {AppButtonAuth} from "ui/base/appButton/AppButtonAuth.jsx";
import {HelperText as BaseHelperText} from "ui/base/helperText/HelperText.jsx";
import {InputSignUp} from "ui/base/InputSignUp/InputSignUp";
import {Modal} from "ui/base/modal/Modal.jsx";
import {EmailIcon, LockIcon} from "ui/icons/index.js";
import {capitalizeFirstLetter, emailValidation, removeEmojis,} from "utils";
import {pixelTrackEvent} from "utils/facebookPixel.js";
import {mediaSizes, themeColor} from "ui/theme";

export const LoginModal = ({show, handleClose, setSignupModal, setForgotPasswordModal}) => {
    const {loginWithCustomToken} = useAuth();

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        platform: "web",
    });

    const [formErrors, setFormErrors] = useState({
        email: "",
        password: "",
    });

    const onChangeInput = (key, value) => {
        const isError = value.trim() === "" || !value;
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }));
        setFormErrors((prevState) => ({
            ...prevState,
            [key]: isError ? `${capitalizeFirstLetter(key)} must be filled ` : "",
        }));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSubmit()
        }
    };

    const {mutate, isLoading} = useMutation({
        mutationFn: login,
        onSuccess: async (data) => {
            await loginWithCustomToken({
                firebaseToken: data.token_firebase,
                appToken: data.token,
            });
            handleClose();
        },
        onError: (err) => {
            const error = err?.response?.data;

            if (error.type === "InvalidPassword") {
                setFormErrors((prevState) => ({
                    ...prevState,
                    password: "Enter correct password",
                }));
            }

            if (error.type === "InvalidEmail") {
                setFormErrors((prevState) => ({
                    ...prevState,
                    email: "This email is not registered with us",
                }));
            }

            if (error.type === "inactive") {
                useToast("error", "Inactive");
            }

            if (error.type === "blocked") {
                useToast("error", "Access to your account has been restricted");
            }

            if (error.type === "unknown") {
                useToast("error", "Login failed");
            }
        },
    });

    const onSubmit = () => {
        pixelTrackEvent("Login: CLicked on Log in");
        if (!emailValidation(formData.email)) {
            setFormErrors((prevState) => ({
                ...prevState,
                email:
                    formData.email?.length > 0
                        ? "Enter correct email"
                        : "Email must be filled",
            }));
        } else if (formData.email !== "" && formData.password !== "") {
            mutate({...formData, email: formData.email.toLocaleLowerCase()});
        } else {
            Object.keys(formData).map((item) => {
                const isError = formData[item].trim() === "";
                setFormErrors((prevState) => ({
                    ...prevState,
                    [item]: isError
                        ? `${capitalizeFirstLetter(item)} must be filled `
                        : "",
                }));
            });
        }
    };

    useEffect(() => {
        pixelTrackEvent("Enter Login page");
    }, []);

    return (
        <Modal show={show} handleClose={handleClose} maxWidth={450}>
            <LoginContainer>
                <LoginTextContainer>
                    <TitleText
                        centerMobile
                        center
                    >
                        Sign in to continue
                    </TitleText>
                </LoginTextContainer>

                <LoginFormContainer>
                    <InputSignUp
                        id="email"
                        icon={<EmailIcon/>}
                        placeholder="Email"
                        value={formData.email}
                        onKeyDown={handleKeyDown}
                        setValue={(input) =>
                            onChangeInput("email", removeEmojis(input.trim()))
                        }
                        isError={formErrors?.email}
                        errorText={formErrors?.email}
                    />
                    <InputSignUp
                        id="password"
                        icon={<LockIcon/>}
                        placeholder="Password"
                        type="password"
                        onKeyDown={handleKeyDown}
                        value={formData.password}
                        setValue={(input) =>
                            onChangeInput("password", removeEmojis(input.trim()))
                        }
                        isError={formErrors?.password}
                        errorText={formErrors?.password}
                    />

                    <LinkHelperText
                        color="placeholder"
                        centerMobile
                        center
                        weight={300}
                        onClick={() => {
                            pixelTrackEvent("Login: Clicked on Forgot Password");
                            handleClose();
                            setForgotPasswordModal(true);
                        }}
                    >
                        Forgot password?
                    </LinkHelperText>

                    <AppButtonAuth onClick={onSubmit} disabled={isLoading}>
                        Log in
                    </AppButtonAuth>
                    <SignUpButton
                        onClick={() => {
                            pixelTrackEvent("Login: CLicked on Sign up");
                            handleClose();
                            setSignupModal(true);
                        }}
                    >
                        Sign up
                    </SignUpButton>
                </LoginFormContainer>
            </LoginContainer>
        </Modal>
    );
};

const LoginContainer = styled.div`
    display: grid;
    gap: 20px;
    width: 100%;
    margin-top: 0.3rem;

    @media (width >= ${mediaSizes.md}) {
        margin-top: 0;
        gap: 30px; 
        padding: 0.5rem 1.5rem 0.5rem;
    }
`;

const LoginTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
`;

const LoginFormContainer = styled.div`
    display: grid;
    gap: 0.7rem;
`;

const TitleText = styled.h1`
    font-family: var(--font-primary);
    font-size: 19px;
    font-weight: 400;

    @media (width >= ${mediaSizes.md}) {
        font-size: 26px;
    }
`;



const SignUpButton = styled.button`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 13px 30px;
    gap: 10px;
    width: 100%;
    height: 50px;
    background: transparent;
    border: 2px solid ${themeColor("basicBlack")};
    border-radius: 500px;
    cursor: pointer;
    font-family: var(--font-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    text-transform: capitalize;
    color: ${themeColor("basicBlack")};
    &:hover {
        opacity: 0.7;
    }
`;

const LinkHelperText = styled(BaseHelperText)`
    font-family: var(--font-primary);
    font-size: 16px;
    margin: 0.9rem auto;
    color: var(--text-color);
    cursor: pointer;
    transition: color 0.2s ease;
    width: max-content;

    &:hover {
        color: ${themeColor("blue")};
    }
`;
